import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import React, { useCallback, useEffect, useMemo } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import { GridRowParams, GridFilterModel } from "@mui/x-data-grid-pro"

import { AsyncRequestStatusSummary } from "./AsyncRequestStatusSummary"
import { AsyncRequestsStore, IAsyncRequestListItem } from "./store"
import { useGetAsyncRequestsColumns } from "./hooks/useGetAsyncRequestsColumns"
import useRouteParams from "./hooks/useRouteParams"

import { ListPage } from "src/components/ListPage"
import { DataGridProTable } from "src/components/Table/DataGridPro"
import { useInitializer } from "src/lib/initializer"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { GlobalStore } from "src/store"
import { AsyncRequestDetailModal } from "src/modals/async-request-detail"

import {
    getFilterModelForRepository,
    getSortModelForRepository,
} from "src/lib/data-grid-pro"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import {
    IAdvancedOperations,
    FilterModel,
    SortModel,
} from "src/types/data-grid-pro"
import { Repository } from "src/types/channel"
import { IDateRangeValue } from "src/types/date_range_picker"

const repository: Repository = "async-request"

const View = observer(() => {
    const store = useStore(AsyncRequestsStore)
    const globalStore = useStore(GlobalStore)
    const _location = useLocation()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const columns = useGetAsyncRequestsColumns()
    const allParams = useRouteParams()

    const advancedOperationsModel: IAdvancedOperations = useMemo(
        () => ({
            pagination: "server",
            filtering: "server",
            sorting: "server",
        }),
        [],
    )

    const getDataGridInitialFilteringState = useCallback(():
        | GridFilterModel
        | undefined => {
        // Filter out date range params since they're handled separately
        const filterParams = allParams.filter(
            (param) => param.param !== "date_from" && param.param !== "date_to",
        )
        if (filterParams.length === 0) return undefined

        const params = store.handleParams(columns, filterParams)
        if (params?.length === 0) return undefined

        return { items: params }
    }, [columns, allParams, store])

    const advanceQuery = useMemo(() => {
        const filter = getFilterModelForRepository(
            repository,
            globalStore.session.dataGridFilterModel,
        )
        const sort = getSortModelForRepository(
            repository,
            globalStore.session.dataGridSortModel,
        )

        // Apply date range filtering
        return store.getAdvanceQueryWithDateFilter({ sort, filter })
    }, [
        globalStore.session.dataGridFilterModel,
        globalStore.session.dataGridSortModel,
        store,
    ])

    // Initialize date range from URL parameters once
    useEffect(() => {
        const dateFromParam = allParams.find(
            (param) => param.param === "date_from",
        )
        const dateToParam = allParams.find((param) => param.param === "date_to")

        if (dateFromParam != null || dateToParam != null) {
            const dateRange: IDateRangeValue = {
                from:
                    dateFromParam != null
                        ? new Date(dateFromParam.value)
                        : null,
                to: dateToParam != null ? new Date(dateToParam.value) : null,
            }
            // Apply date range but don't update URL (already set)
            void store.applyDateRangeFilter(dateRange)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Only run once on mount

    const initialized = useInitializer(async () => {
        const accessGroupId =
            globalStore.session.accessGroupId ?? DEFAULT_ACCESS_GROUP.id
        await store.init(accessGroupId, advanceQuery)
    }, [globalStore.session.accessGroupId, advanceQuery])

    const openAsyncRequestModal = useCallback(
        (requestId: number) => {
            globalStore.modals.open(
                () => (
                    <AsyncRequestDetailModal
                        id={requestId}
                        onClose={() => navigate("/async-requests")}
                    />
                ),
                {
                    onClose: () => navigate("/async-requests"),
                },
            )
        },
        [globalStore.modals, navigate],
    )

    useEffect(() => {
        if (!initialized) return
        if (id != null && !isNaN(Number(id))) {
            openAsyncRequestModal(Number(id))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized, id])

    useEffect(() => {
        return () => {
            store.dispose()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleAsyncRequestClick = useCallback(
        (item: IAsyncRequestListItem) => {
            navigate(`/async-requests/${item.id}`)
        },
        [navigate],
    )

    const handleFilterChange = useCallback(
        async (model: FilterModel) => {
            const sort = getSortModelForRepository(
                repository,
                globalStore.session.dataGridSortModel,
            )

            // Update URL with filter parameters
            const params = new URLSearchParams()

            // Preserve date range parameters if they exist
            const dateFromParam = allParams.find(
                (param) => param.param === "date_from",
            )
            const dateToParam = allParams.find(
                (param) => param.param === "date_to",
            )
            if (dateFromParam != null) {
                params.set("date_from", dateFromParam.value)
            }
            if (dateToParam != null) {
                params.set("date_to", dateToParam.value)
            }

            // Add grid filter parameters
            model.items.forEach((item) => {
                if (item.value !== undefined) {
                    params.set(item.field, String(item.value))
                }
            })

            const url =
                params.toString() !== ""
                    ? `/async-requests?${params.toString()}`
                    : "/async-requests"
            navigate(url, { replace: true })

            const queryWithDateFilter = store.getAdvanceQueryWithDateFilter({
                sort,
                filter: model,
            })
            if (queryWithDateFilter != null) {
                await store.query(queryWithDateFilter)
            }
        },
        [globalStore.session.dataGridSortModel, store, navigate, allParams],
    )

    const handleSortChange = useCallback(
        async (model: SortModel) => {
            const filter = getFilterModelForRepository(
                repository,
                globalStore.session.dataGridFilterModel,
            )
            const queryWithDateFilter = store.getAdvanceQueryWithDateFilter({
                filter,
                sort: model,
            })
            if (queryWithDateFilter != null) {
                await store.query(queryWithDateFilter)
            }
        },
        [globalStore.session.dataGridFilterModel, store],
    )

    const handleDateRangeChange = useCallback(
        async (dateRange: IDateRangeValue) => {
            // Update URL with date range parameters
            const params = new URLSearchParams()

            // Add date range parameters
            if (dateRange.from != null) {
                params.set(
                    "date_from",
                    dateRange.from.toISOString().split("T")[0],
                )
            }
            if (dateRange.to != null) {
                params.set("date_to", dateRange.to.toISOString().split("T")[0])
            }

            // Preserve existing grid filter parameters
            allParams.forEach((param) => {
                if (param.param !== "date_from" && param.param !== "date_to") {
                    params.set(param.param, param.value)
                }
            })

            const url =
                params.toString() !== ""
                    ? `/async-requests?${params.toString()}`
                    : "/async-requests"
            navigate(url, { replace: true })

            // Update date range and refresh summary
            await store.applyDateRangeFilter(dateRange)

            // Get current grid filter and sort state
            const filter = getFilterModelForRepository(
                repository,
                globalStore.session.dataGridFilterModel,
            )
            const sort = getSortModelForRepository(
                repository,
                globalStore.session.dataGridSortModel,
            )

            // Apply date range filter to grid
            const queryWithDateFilter = store.getAdvanceQueryWithDateFilter({
                sort,
                filter,
            })
            if (queryWithDateFilter != null) {
                await store.query(queryWithDateFilter)
            }
        },
        [
            store,
            globalStore.session.dataGridFilterModel,
            globalStore.session.dataGridSortModel,
            navigate,
            allParams,
        ],
    )

    const handleProcessingClick = useCallback(async () => {
        const filterModel: FilterModel = {
            items: [
                {
                    field: "status",
                    operator: "equals",
                    value: "processing",
                },
            ],
        }
        await handleFilterChange(filterModel)
    }, [handleFilterChange])

    const header = useMemo(
        () => ({
            header: t`async-requests.header`,
            breadcrumbs: [t`async-requests.breadcrumb`],
        }),
        [],
    )

    return (
        <ListPage header={header} loading={!initialized}>
            <AsyncRequestStatusSummary
                statusSummary={store.statusSummary}
                dateRangeFilter={store.dateRangeFilter}
                onDateRangeChange={handleDateRangeChange}
                getStatusColor={store.getStatusColor.bind(store)}
                getStatusText={store.getStatusText.bind(store)}
                onProcessingClick={handleProcessingClick}
            />
            <DataGridProTable
                paginator={store.asyncRequests}
                data={store.asyncRequests.items}
                columns={columns}
                advancedOperations={advancedOperationsModel}
                repository={repository}
                loading={!initialized}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onRowClickEvent={(params: GridRowParams) =>
                    handleAsyncRequestClick(params.row as IAsyncRequestListItem)
                }
                filteringInitialState={getDataGridInitialFilteringState()}
            />
        </ListPage>
    )
})

export const AsyncRequestsView = () => (
    <StoreProvider Store={AsyncRequestsStore}>
        <View />
    </StoreProvider>
)
